import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { tipsUrl, tr } from "@/lib/translations";
import { TIP_TOPICS, TOPIC_ICONS, topicLabel } from "@/lib/tipTopics";

const PER_TOPIC = 4;

/** Inselseite: Reisetipps der Insel nach Thema gruppiert – jede Themenkarte verlinkt auf die gefilterte Übersicht /reisetipps?island=…&topic=…. */
export const IslandTips = ({ island, name }) => {
  const { t, lang } = useLang();
  const [tips, setTips] = useState([]);
  useEffect(() => { api.get("/pages/tips", { params: { island } }).then((r) => setTips(r.data)).catch(() => setTips([])); }, [island]);
  if (!tips.length) return null;
  const groups = TIP_TOPICS.map((k) => ({ key: k, items: tips.filter((p) => p.topic === k) })).filter((g) => g.items.length);
  const rest = tips.filter((p) => !TIP_TOPICS.includes(p.topic));
  const url = (topic) => `${tipsUrl()}?island=${island}${topic ? `&topic=${topic}` : ""}`;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="island-tips">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand flex items-center gap-2"><BookOpen className="w-4 h-4" />{t.tips.islandTipsSub}</div>
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mt-3">{t.tips.forIsland.replace("{n}", name)}</h2>
        </div>
        <Link to={url()} className="text-sm text-brand font-semibold hover:underline underline-offset-4 whitespace-nowrap" data-testid="island-tips-all">{t.tips.showAll.replace("{n}", tips.length).replace("{i}", name)} →</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {groups.map((g) => { const Icon = TOPIC_ICONS[g.key]; return (
          <div key={g.key} className="rounded-2xl border border-stone-200 bg-white p-5 flex flex-col" data-testid={`island-tips-topic-${g.key}`}>
            <Link to={url(g.key)} className="flex items-center gap-2 font-serif text-xl text-stone-900 hover:text-brand transition-colors" data-testid={`island-tips-topic-link-${g.key}`}>
              <span className="inline-flex w-9 h-9 rounded-full bg-tint items-center justify-center shrink-0"><Icon className="w-4 h-4 text-brand" /></span>{topicLabel(t, g.key)}
            </Link>
            <ul className="mt-3 space-y-1.5 text-sm">
              {g.items.slice(0, PER_TOPIC).map((p) => (
                <li key={p.slug}><Link to={p.path} className="text-stone-700 hover:text-brand hover:underline underline-offset-4 line-clamp-1" data-testid={`island-tip-${p.slug}`}>{tr(p.title, lang)}</Link></li>
              ))}
            </ul>
            {g.items.length > PER_TOPIC && (
              <Link to={url(g.key)} className="mt-auto pt-3 inline-flex items-center gap-1 text-sm text-brand font-semibold hover:gap-2 transition-all" data-testid={`island-tips-more-${g.key}`}>{t.tips.count.replace("{n}", g.items.length)}<ArrowRight className="w-4 h-4" /></Link>
            )}
          </div>); })}
        {rest.length > 0 && (
          <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="island-tips-topic-other">
            <ul className="space-y-1.5 text-sm">{rest.slice(0, PER_TOPIC).map((p) => <li key={p.slug}><Link to={p.path} className="text-stone-700 hover:text-brand hover:underline underline-offset-4 line-clamp-1">{tr(p.title, lang)}</Link></li>)}</ul>
          </div>
        )}
      </div>
    </section>
  );
};
